/**
 * Глобальный чат + комментарии (AI SDK v7).
 *
 * Смонтирован ОДИН раз на уровне App (GlobalChatDrawer) — навигация между
 * страницами не анмаунтит его: стрим продолжается, пока пользователь
 * изучает модель; «ответ готов» подсвечивается индикатором на FAB.
 * Контекст страницы (owner/repo/ref/path/pageContext) — chat-store,
 * транспорт читает его лениво на каждый запрос.
 *
 * v7: useChat из @ai-sdk/react + DefaultChatTransport; сообщения —
 * UIMessage.parts (text / tool-*); клиентские тулы (post_comment,
 * create_issue) — карточки предпросмотра, публикует клиент через свои
 * эндпоинты, продолжение через sendAutomaticallyWhen; ui_navigate /
 * ui_highlight / ui_tour исполняет клиент по output-available.
 *
 * Лента — use-stick-to-bottom (как в Vercel AI Elements): ручной скролл
 * отключает прилипание, кнопка «вниз» возвращает. Сворачивание не теряет
 * диалог (контент скрывается, не анмаунтится).
 */

import {
    ActionIcon, Anchor, Avatar, Badge, Box, Button, CloseButton, Divider,
    Flex, Group, Indicator, Loader, Menu, Paper, ScrollArea, Stack,
    Tabs, Text, Textarea, Tooltip, TypographyStylesProvider,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
    IconArrowDown, IconCheck, IconHistory,
    IconMessageCircle, IconMessages, IconPlayerStop, IconPlus, IconSend,
    IconSparkles, IconTrash,
} from '@tabler/icons-react';
import { useChat } from '@ai-sdk/react';
import type { UIMessage } from 'ai';
import { getProjectChat, getSessionId, markRestored, rollSessionId, setSessionId as setChatSessionId } from '../lib/chat-instance';
import type { ChatMessageItem } from '../lib/api-client';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createRepoIssue, deleteChatSession, fetchChatMessages, fetchChatSessions,
    fetchComments, postComment, type ChatSessionItem, type PflowComment,
} from '../lib/api-client';
import { highlightTarget, runTour, PLAN_TOUR, REPORTS_TOUR, WELCOME_TOUR } from '../lib/tours';
import { linkifyMentionsInMarkdown, MentionAwareLink } from '../lib/entity-mentions';
import { CommentThread } from './CommentThread';
import { useMe } from '../hooks/use-me';
import { useUiStore } from '../stores/ui-store';
import { useChatStore, type ChatPageCtx } from '../stores/chat-store';
import type { ChatPageContext } from '@pflow/shared/v2';


function relativeDate(ts: number): string {
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60_000);
    if (m < 1)  return 'только что';
    if (m < 60) return `${m} мин. назад`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ч. назад`;
    return `${Math.floor(h / 24)} д. назад`;
}

function deriveTarget(ctx?: ChatPageContext): string {
    if (!ctx || ctx.view !== 'canvas') return '';
    if (ctx.openEntity) return `${ctx.openEntity.type}."${ctx.openEntity.name}"`;
    if (ctx.canvas)     return `Канвас."${ctx.canvas}"`;
    return '';
}

function targetLabel(target: string): string {
    if (!target) return 'Проект';
    return target;
}

// ─── v7 tool-part helpers ───────────────────────────────────────────────────

interface AnyToolPart {
    type:       string;
    toolCallId: string;
    state:      string;
    input?:     unknown;
    output?:    unknown;
    toolName?:  string;
}

function isToolPart(p: { type: string }): p is AnyToolPart {
    return p.type.startsWith('tool-') || p.type === 'dynamic-tool';
}

function toolNameOf(p: AnyToolPart): string {
    return p.type === 'dynamic-tool' ? (p.toolName ?? '') : p.type.slice('tool-'.length);
}

const TOOL_LABELS: Record<string, string> = {
    pflow_overview:       'Изучаю проект',
    pflow_list:           'Просматриваю сущности',
    pflow_context:        'Читаю контекст',
    pflow_aspect_list:    'Смотрю нарративы',
    pflow_aspect_detail:  'Читаю нарратив',
    pflow_entity_history: 'Смотрю историю',
    pflow_diff_overview:  'Сравниваю версии',
    pflow_diff_aspect:    'Читаю дельту нарратива',
    pflow_diff_entity:    'Сравниваю сущность',
    post_comment:         'Публикую комментарий',
    create_issue:         'Создаю issue',
    ui_navigate:          'Переключаю страницу',
    ui_highlight:         'Показываю в интерфейсе',
    ui_tour:              'Запускаю тур',
};

/** Завершённые шаги — совершенный вид («✓ Прочитал контекст ×3»). */
const TOOL_DONE_LABELS: Record<string, string> = {
    pflow_overview:       'Изучил проект',
    pflow_list:           'Просмотрел сущности',
    pflow_context:        'Прочитал контекст',
    pflow_aspect_list:    'Посмотрел нарративы',
    pflow_aspect_detail:  'Прочитал нарратив',
    pflow_entity_history: 'Посмотрел историю',
    pflow_diff_overview:  'Сравнил версии',
    pflow_diff_aspect:    'Прочитал дельту нарратива',
    pflow_diff_entity:    'Сравнил сущность',
    ui_navigate:          'Переключил страницу',
    ui_highlight:         'Показал в интерфейсе',
    ui_tour:              'Запустил тур',
};

const SUGGESTIONS = [
    'Расскажи, что это за проект',
    'Что сейчас в плане проверки гипотез?',
    'Проведи меня по интерфейсу',
    'Что изменилось в модели за последнее время?',
];

/** Тулы, требующие подтверждения человеком перед публикацией. */
const CONFIRM_TOOLS = new Set(['post_comment', 'create_issue']);

/** DB-запись → UIMessage: восстанавливает полные parts (текст + tool-вызовы),
 *  fallback на текст для старых записей без parts. */
function restoreMessage(m: ChatMessageItem): UIMessage {
    let parts: UIMessage['parts'] | null = null;
    if (m.parts) {
        try { parts = JSON.parse(m.parts) as UIMessage['parts']; } catch { parts = null; }
    }
    return {
        id:    m.id,
        role:  m.role,
        parts: parts && parts.length > 0 ? parts : [{ type: 'text' as const, text: m.content }],
    };
}

// ─── CommentsTab ────────────────────────────────────────────────────────────

function CommentsTab({
    owner, repo, path, target, onSwitchToChat,
}: {
    owner: string; repo: string; path?: string; target: string;
    onSwitchToChat: (prefill: string) => void;
}) {
    const meQ = useMe();
    const isAuthed = !!meQ.data?.user;
    const queryClient = useQueryClient();
    const commentsKey = ['comments', owner, repo, path] as const;
    const q = useQuery({
        queryKey:             commentsKey,
        queryFn:              () => fetchComments({ owner, repo, path }),
        staleTime:            0,
        refetchInterval:      20_000,
        refetchOnWindowFocus: true,
    });

    const invalidate = (): void => {
        void queryClient.invalidateQueries({ queryKey: commentsKey });
        void queryClient.invalidateQueries({ queryKey: ['all-comments', owner, repo, path] });
    };

    // Новое обсуждение к текущей сущности — прямой POST (ответы в тредах —
    // инлайн в CommentThread, без агента). Если сущность не открыта, target
    // пуст → комментарий к проекту.
    const [draft, setDraft] = useState('');
    const postM = useMutation({
        mutationFn: () => postComment({ owner, repo, target, text: draft.trim(), path }),
        onSuccess:  (res) => {
            setDraft('');
            queryClient.setQueryData<{ comments: PflowComment[] }>(commentsKey, prev =>
                prev ? { comments: [...prev.comments, res.comment] } : { comments: [res.comment] });
            setTimeout(invalidate, 3000);
        },
    });

    if (q.isLoading) {
        return <Text fz="sm" c="dimmed" ta="center" mt="xl">Загрузка…</Text>;
    }
    if (q.isError) {
        return <Text fz="sm" c="red" ta="center" mt="xl">Ошибка загрузки обсуждений</Text>;
    }

    const comments = q.data?.comments ?? [];
    // Новейшие сверху — недавние обсуждения ближе.
    const ordered = [...comments].reverse();

    return (
        <Stack gap="md" p="md">
            {comments.length === 0 ? (
                <Stack gap="xs" align="center" mt="lg">
                    <IconMessages size={32} color="var(--mantine-color-dimmed)" />
                    <Text fz="sm" c="dimmed" ta="center">Обсуждений пока нет.</Text>
                </Stack>
            ) : (
                <Stack gap="sm">
                    {ordered.map(c => (
                        <CommentThread key={c.id} owner={owner} repo={repo} comment={c}
                            isAuthed={isAuthed} showTarget onChanged={invalidate} />
                    ))}
                </Stack>
            )}

            {/* Новое обсуждение к открытой сущности (или к проекту). */}
            {isAuthed ? (
                <Stack gap={6}>
                    <Text fz="xs" c="dimmed">
                        Новое обсуждение{target ? ` к ${target}` : ' к проекту'}:
                    </Text>
                    <Textarea
                        placeholder="Начать обсуждение…"
                        autosize minRows={2} size="xs"
                        value={draft}
                        onChange={e => setDraft(e.currentTarget.value)}
                    />
                    <Group justify="space-between" gap={8}>
                        <Button size="compact-xs" variant="subtle" color="gray"
                            onClick={() => onSwitchToChat(target ? `Про ${target}: ` : 'Про проект: ')}>
                            Спросить агента
                        </Button>
                        <Button size="xs" variant="light"
                            disabled={draft.trim().length === 0}
                            loading={postM.isPending}
                            onClick={() => postM.mutate()}>
                            Начать обсуждение
                        </Button>
                    </Group>
                    {postM.isError && <Text fz="xs" c="red.7">{(postM.error as Error).message}</Text>}
                </Stack>
            ) : (
                <Text fz="xs" c="dimmed">Войди через GitHub, чтобы обсуждать.</Text>
            )}
        </Stack>
    );
}

// ─── ChatCore: лента + композер (AI SDK v7) ─────────────────────────────────

function ChatCore({
    owner, repo, path, pageContext, prefill,
}: {
    owner: string; repo: string; path?: string;
    pageContext?: ChatPageContext; prefill?: string;
}) {
    const [input, setInput]         = useState('');
    const textareaRef               = useRef<HTMLTextAreaElement>(null);
    const queryClient               = useQueryClient();
    const navigate                  = useNavigate();

    // Chat-инстанс живёт ВНЕ React (модульный стор) — переживает ремаунт
    // при повороте экрана и сворачивании. Стабилен per owner/repo.
    const { chat, id: chatId } = useMemo(() => getProjectChat(owner, repo), [owner, repo]);

    const {
        messages, sendMessage, status, error, setMessages, stop, addToolResult,
    } = useChat({ chat });
    const isLoading = status === 'submitted' || status === 'streaming';

    // Стрим завершился: «ответ готов» (индикатор на FAB, если чат свёрнут) +
    // обновляем список сессий (onFinish жил в useChat, теперь у Chat-инстанса
    // его нет — ловим по смене статуса).
    const prevStatus = useRef(status);
    useEffect(() => {
        const wasActive = prevStatus.current === 'streaming' || prevStatus.current === 'submitted';
        if (wasActive && status === 'ready') {
            if (!useChatStore.getState().open) useChatStore.getState().setUnread(true);
            void queryClient.invalidateQueries({ queryKey: ['chat-sessions', owner, repo] });
        }
        prevStatus.current = status;
    }, [status, queryClient, owner, repo]);

    // ── Клиентские публикации (post_comment / create_issue) ─────────────
    const [busyToolId, setBusyToolId] = useState<string | null>(null);
    const publishTool = async (p: AnyToolPart): Promise<void> => {
        const name = toolNameOf(p);
        const args = (p.input ?? {}) as Record<string, unknown>;
        setBusyToolId(p.toolCallId);
        try {
            if (name === 'post_comment') {
                const target = typeof args.target === 'string' && args.target
                    ? args.target
                    : deriveTarget(useChatStore.getState().ctx?.pageContext);
                const res = await postComment({
                    owner, repo, target,
                    text: String(args.text ?? ''), path,
                });
                addToolResult({ tool: name, toolCallId: p.toolCallId, output: { ok: true, url: res.comment.url, id: res.comment.id, target } });
                void queryClient.invalidateQueries({ queryKey: ['comments', owner, repo] });
                void queryClient.invalidateQueries({ queryKey: ['entity-comments', owner, repo] });
                void queryClient.invalidateQueries({ queryKey: ['all-comments', owner, repo] });
            } else if (name === 'create_issue') {
                const labels = Array.isArray(args.labels) ? args.labels.filter((l): l is string => typeof l === 'string') : [];
                const res = await createRepoIssue({
                    owner, repo,
                    title: String(args.title ?? ''),
                    body:  typeof args.body === 'string' ? args.body : '',
                    labels, path,
                });
                addToolResult({ tool: name, toolCallId: p.toolCallId, output: { ok: true, number: res.number, url: res.url } });
                void queryClient.invalidateQueries({ queryKey: ['repo-issues', owner, repo] });
            }
        } catch (e) {
            addToolResult({ tool: name, toolCallId: p.toolCallId, output: { ok: false, error: String(e) } });
        } finally {
            setBusyToolId(null);
        }
    };
    const cancelTool = (p: AnyToolPart): void => {
        addToolResult({
            tool: toolNameOf(p), toolCallId: p.toolCallId,
            output: { ok: false, cancelled: true, note: 'Пользователь отменил публикацию.' },
        });
    };

    // ── Исполнитель UI-тулов (echo-паттерн, output-available) ────────────
    const executedUiCalls = useRef(new Set<string>());
    useEffect(() => {
        for (const m of messages) {
            for (const part of m.parts) {
                if (!isToolPart(part)) continue;
                const name = toolNameOf(part);
                if (!name.startsWith('ui_')) continue;
                if (part.state !== 'output-available') continue;
                if (executedUiCalls.current.has(part.toolCallId)) continue;
                executedUiCalls.current.add(part.toolCallId);

                const args = (part.input ?? {}) as Record<string, unknown>;
                const ctx  = useChatStore.getState().ctx;
                if (name === 'ui_navigate') {
                    const page = String(args.page ?? 'narrative');
                    const q = new URLSearchParams();
                    if (ctx?.gitRef) q.set('ref', ctx.gitRef);
                    if (ctx?.path)   q.set('path', ctx.path);
                    const qs   = q.toString() ? `?${q.toString()}` : '';
                    const base = `/r/${owner}/${repo}`;
                    navigate(page === 'narrative' ? `${base}${qs}` : `${base}/${page}${qs}`);
                } else if (name === 'ui_highlight') {
                    const target = String(args.target ?? '');
                    const text   = String(args.text ?? '');
                    const title  = typeof args.title === 'string' ? args.title : undefined;
                    setTimeout(() => highlightTarget(target, text, title), 400);
                } else if (name === 'ui_tour') {
                    const tour = location.pathname.includes('/plan') ? PLAN_TOUR
                        : location.pathname.includes('/reports') ? REPORTS_TOUR
                        : WELCOME_TOUR;
                    setTimeout(() => runTour(tour), 300);
                }
            }
        }
    }, [messages, navigate, owner, repo]);

    // ── Сессии ───────────────────────────────────────────────────────────
    const sessionsQ = useQuery({
        queryKey: ['chat-sessions', owner, repo],
        queryFn:  () => fetchChatSessions({ owner, repo }),
        staleTime: 5_000,
    });

    useEffect(() => {
        if (prefill !== undefined) {
            setInput(prefill);
            setTimeout(() => textareaRef.current?.focus(), 50);
        }
    }, [prefill]);

    const startNewChat = useCallback(() => {
        rollSessionId(chatId);   // новый sessionId для персистентности
        setMessages([]);         // Chat не пересоздаётся — просто чистим ленту
    }, [chatId, setMessages]);

    const loadSession = useCallback(async (s: ChatSessionItem) => {
        try {
            const { messages: saved } = await fetchChatMessages({ owner, repo, sessionId: s.id });
            setChatSessionId(chatId, s.id);   // продолжаем эту сессию — уйдёт в body
            setMessages(saved.map(restoreMessage));
        } catch { /* ignore */ }
    }, [owner, repo, chatId, setMessages]);

    // ── Восстановление после перезагрузки страницы ──────────────────────
    // Модульный стор в памяти — reload его чистит. sessionId переживает reload
    // (localStorage); подгружаем сообщения этой сессии один раз на инстанс,
    // если лента пуста и стрим не идёт.
    useEffect(() => {
        if (messages.length > 0 || isLoading) return;
        if (!markRestored(chatId)) return;
        const sid = getSessionId(chatId);
        if (!sid) return;
        void fetchChatMessages({ owner, repo, sessionId: sid })
            .then(({ messages: saved }) => {
                if (saved.length === 0) return;
                // Только если пользователь не начал новый диалог, пока грузили.
                setMessages(prev => prev.length === 0 ? saved.map(restoreMessage) : prev);
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeSession = useCallback(async (s: ChatSessionItem, e: React.MouseEvent) => {
        e.stopPropagation();
        await deleteChatSession({ owner, repo, sessionId: s.id }).catch(() => undefined);
        void queryClient.invalidateQueries({ queryKey: ['chat-sessions', owner, repo] });
        if (s.id === getSessionId(chatId)) startNewChat();
    }, [owner, repo, chatId, queryClient, startNewChat]);

    const submit = (): void => {
        const text = input.trim();
        if (!text || isLoading) return;
        setInput('');
        void sendMessage({ text });
    };

    const sessions = sessionsQ.data?.sessions ?? [];

    return (
        <Flex direction="column" style={{ flex: 1, minHeight: 0 }}>
            {/* Шапка вкладки: история + новый чат */}
            <Group px="md" py={6} gap="xs" justify="flex-end"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
                <Menu shadow="md" width={280} position="bottom-end">
                    <Menu.Target>
                        <Tooltip label="История чатов" position="left">
                            <ActionIcon size="sm" variant="subtle" aria-label="История">
                                <IconHistory size={15} />
                            </ActionIcon>
                        </Tooltip>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Label>Предыдущие чаты</Menu.Label>
                        {sessions.length === 0 && (
                            <Menu.Item disabled>Нет сохранённых чатов</Menu.Item>
                        )}
                        {sessions.map(s => (
                            <Menu.Item key={s.id} onClick={() => void loadSession(s)}
                                rightSection={
                                    <ActionIcon size="xs" variant="subtle" color="red"
                                        onClick={e => void removeSession(s, e)}>
                                        <IconTrash size={12} />
                                    </ActionIcon>
                                }>
                                <Text fz="sm" lineClamp={1}>{s.title || '(без названия)'}</Text>
                                <Text fz="xs" c="dimmed">{relativeDate(s.updatedAt)}</Text>
                            </Menu.Item>
                        ))}
                    </Menu.Dropdown>
                </Menu>
                <Tooltip label="Новый чат" position="left">
                    <ActionIcon size="sm" variant="subtle" onClick={startNewChat} aria-label="Новый чат">
                        <IconPlus size={15} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            {/* Лента: stick-to-bottom + кнопка «вниз» */}
            <StickToBottom
                initial="instant"
                resize="instant"
                style={{ flex: 1, minHeight: 0, position: 'relative', overflowY: 'auto' }}
            >
                <StickToBottom.Content style={{ padding: 16 }}>
                    {messages.length === 0 && !isLoading && (
                        <Stack gap="xs" mt="md">
                            <Group gap={6} justify="center">
                                <IconSparkles size={14} color="var(--mantine-color-dimmed)" />
                                <Text fz="sm" c="dimmed">Спроси о модели — или попробуй:</Text>
                            </Group>
                            {SUGGESTIONS.map(s => (
                                <Button
                                    key={s} variant="light" size="xs" radius="md"
                                    onClick={() => void sendMessage({ text: s })}
                                    styles={{
                                        inner: { justifyContent: 'flex-start' },
                                        label: { whiteSpace: 'normal', textAlign: 'left' },
                                    }}
                                >
                                    {s}
                                </Button>
                            ))}
                        </Stack>
                    )}
                    <Stack gap="md">
                        {messages.map(m => (
                            <MessageView key={m.id} message={m}
                                busyToolId={busyToolId}
                                onPublish={publishTool}
                                onCancel={cancelTool} />
                        ))}
                        {isLoading && lastAssistantIsEmpty(messages) && (
                            <Group gap={6}>
                                <Loader size={12} />
                                <Text fz="sm" c="dimmed">Думаю…</Text>
                            </Group>
                        )}
                        {error && (
                            <Text fz="xs" c="red" ta="center">{error.message}</Text>
                        )}
                    </Stack>
                </StickToBottom.Content>
                <ScrollDownButton />
            </StickToBottom>

            {/* Композер */}
            <Box px="md" pb="md" pt="xs" style={{
                flexShrink:    0,
                paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
            }}>
                <Paper radius="lg" withBorder p={4}>
                    <form onSubmit={e => { e.preventDefault(); submit(); }}
                        style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                        <Textarea flex={1}
                            ref={textareaRef}
                            placeholder="Спросить или прокомментировать…"
                            value={input}
                            onChange={e => setInput(e.currentTarget.value)}
                            autosize minRows={1} maxRows={6} size="sm"
                            variant="unstyled"
                            px={8}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    submit();
                                }
                            }}
                        />
                        {isLoading ? (
                            <Tooltip label="Остановить">
                                <ActionIcon size="lg" radius="xl" variant="light" color="red"
                                    onClick={() => void stop()} aria-label="Остановить генерацию">
                                    <IconPlayerStop size={16} />
                                </ActionIcon>
                            </Tooltip>
                        ) : (
                            <ActionIcon type="submit" size="lg" radius="xl" variant="filled" color="blue"
                                disabled={!input.trim()} aria-label="Отправить">
                                <IconSend size={16} />
                            </ActionIcon>
                        )}
                    </form>
                </Paper>
            </Box>
        </Flex>
    );
}

/** Кнопка «прокрутить вниз»: видна, когда пользователь ушёл от низа ленты. */
function ScrollDownButton(): JSX.Element | null {
    const { isAtBottom, scrollToBottom } = useStickToBottomContext();
    if (isAtBottom) return null;
    return (
        <ActionIcon
            size="lg" radius="xl" variant="filled" color="gray"
            onClick={() => void scrollToBottom()}
            aria-label="Прокрутить вниз"
            style={{
                position: 'absolute', bottom: 12, left: '50%',
                transform: 'translateX(-50%)', opacity: 0.9,
                boxShadow: 'var(--mantine-shadow-md)',
            }}
        >
            <IconArrowDown size={18} />
        </ActionIcon>
    );
}

/** «Думаю…» — пока последний ассистентский месседж без текста. */
function lastAssistantIsEmpty(messages: UIMessage[]): boolean {
    const last = messages[messages.length - 1];
    if (!last) return true;
    if (last.role === 'user') return true;
    return !last.parts.some(p => p.type === 'text' && p.text.trim().length > 0);
}

// ─── Рендер сообщения (v7 parts) ────────────────────────────────────────────

interface ConfirmHandlers {
    busyToolId: string | null;
    onPublish:  (p: AnyToolPart) => void | Promise<void>;
    onCancel:   (p: AnyToolPart) => void;
}

function MessageView({ message, busyToolId, onPublish, onCancel }: { message: UIMessage } & ConfirmHandlers): JSX.Element {
    if (message.role === 'user') {
        const text = message.parts
            .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
            .map(p => p.text).join('');
        return (
            <Flex justify="flex-end">
                <Paper radius="lg" px="sm" py={6}
                    style={{
                        maxWidth: '85%', wordBreak: 'break-word',
                        background: 'var(--mantine-color-default-hover)',
                    }}>
                    <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>{text}</Text>
                </Paper>
            </Flex>
        );
    }

    // Ассистент: текст полноширинной прозой (как Response в AI Elements),
    // подряд идущие завершённые тулы схлопываются в одну строку.
    type Seg =
        | { kind: 'text'; text: string }
        | { kind: 'tools'; parts: AnyToolPart[] }
        | { kind: 'confirm'; part: AnyToolPart };
    const segments: Seg[] = [];
    for (const part of message.parts) {
        if (part.type === 'text') {
            if (part.text.trim()) segments.push({ kind: 'text', text: part.text });
        } else if (isToolPart(part)) {
            if (CONFIRM_TOOLS.has(toolNameOf(part))) {
                segments.push({ kind: 'confirm', part });
            } else {
                const last = segments[segments.length - 1];
                if (last?.kind === 'tools') last.parts.push(part);
                else segments.push({ kind: 'tools', parts: [part] });
            }
        }
    }

    return (
        <Stack gap={6} align="stretch">
            {segments.map((seg, i) => {
                if (seg.kind === 'text') {
                    return (
                        <TypographyStylesProvider key={i} fz="sm"
                            style={{ overflowWrap: 'break-word', minWidth: 0 }}
                            styles={{ root: {
                                fontSize: 'var(--mantine-font-size-sm)',
                                '& > :first-child': { marginTop: 0 },
                                '& > :last-child':  { marginBottom: 0 },
                            }}}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}
                                components={{ a: MentionAwareLink }}>
                                {linkifyMentionsInMarkdown(seg.text)}
                            </ReactMarkdown>
                        </TypographyStylesProvider>
                    );
                }
                if (seg.kind === 'confirm') {
                    return (
                        <ConfirmToolCard key={i} part={seg.part}
                            busy={busyToolId === seg.part.toolCallId}
                            onPublish={() => void onPublish(seg.part)}
                            onCancel={() => onCancel(seg.part)} />
                    );
                }
                return <ToolGroup key={i} parts={seg.parts} />;
            })}
        </Stack>
    );
}

/**
 * Группа tool-вызовов: компактная строка «✓ Прочитал контекст ×3» +
 * активный чип со спиннером. Клик по строке раскрывает детали каждого
 * вызова — аргументы и превью результата («что именно читал»).
 */
function ToolGroup({ parts }: { parts: AnyToolPart[] }): JSX.Element {
    const [open, setOpen] = useState(false);
    const done   = parts.filter(p => p.state === 'output-available' || p.state === 'output-error');
    const active = parts.filter(p => p.state === 'input-streaming' || p.state === 'input-available');

    // Дедуп повторных вызовов одного тула: «Прочитал контекст ×3».
    const doneCounts = new Map<string, number>();
    for (const p of done) {
        const name  = toolNameOf(p);
        const label = TOOL_DONE_LABELS[name] ?? TOOL_LABELS[name] ?? name;
        doneCounts.set(label, (doneCounts.get(label) ?? 0) + 1);
    }
    const doneLine = [...doneCounts.entries()]
        .map(([label, n]) => n > 1 ? `${label} ×${n}` : label)
        .join(' · ');

    const compactJson = (v: unknown, max: number): string => {
        try {
            const s = typeof v === 'string' ? v : JSON.stringify(v ?? {});
            return s.length > max ? s.slice(0, max) + '…' : s;
        } catch { return '<?>'; }
    };

    return (
        <Box>
            <Group gap={6} wrap="wrap">
                {done.length > 0 && (
                    <Text fz="xs" c="dimmed" style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setOpen(o => !o)}
                        title={open ? 'Скрыть детали вызовов' : 'Показать аргументы и результаты'}>
                        ✓ {doneLine} {open ? '▾' : '▸'}
                    </Text>
                )}
                {active.map(p => (
                    <Badge key={p.toolCallId} size="sm" variant="light" color="blue"
                        leftSection={<Loader size={10} />}
                        style={{ textTransform: 'none', fontWeight: 500 }}>
                        {TOOL_LABELS[toolNameOf(p)] ?? toolNameOf(p)}
                    </Badge>
                ))}
            </Group>
            {open && (
                <Stack gap={4} mt={4} pl={8}
                    style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
                    {done.map(p => {
                        const name = toolNameOf(p);
                        const args = compactJson(p.input, 160);
                        return (
                            <Box key={p.toolCallId}>
                                <Text fz="xs" fw={600} c="dimmed">
                                    {name}{args !== '{}' ? ` ${args}` : ''}
                                </Text>
                                <Text fz="xs" c="dimmed" ff="monospace"
                                    style={{ wordBreak: 'break-all', opacity: 0.8 }}>
                                    → {p.state === 'output-error' ? 'ошибка' : compactJson(p.output, 280)}
                                </Text>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </Box>
    );
}

/** Предпросмотр публикации: комментарий или issue от имени пользователя. */
function ConfirmToolCard({ part, busy, onPublish, onCancel }: {
    part: AnyToolPart;
    busy: boolean;
    onPublish: () => void;
    onCancel:  () => void;
}): JSX.Element {
    const args    = (part.input ?? {}) as Record<string, unknown>;
    const isIssue = toolNameOf(part) === 'create_issue';

    if (part.state === 'output-available' || part.state === 'output-error') {
        const r = (part.output ?? {}) as { ok?: boolean; url?: string; cancelled?: boolean; error?: string };
        if (r.ok && r.url) {
            return (
                <Badge size="sm" variant="light" color="green"
                    leftSection={<IconCheck size={11} />}
                    style={{ textTransform: 'none', fontWeight: 500, alignSelf: 'flex-start' }}>
                    <Anchor href={r.url} target="_blank" rel="noreferrer" fz="xs" c="inherit" underline="always">
                        {isIssue ? "Issue создан" : "Опубликовано в обсуждениях"}
                    </Anchor>
                </Badge>
            );
        }
        return (
            <Badge size="sm" variant="light" color="gray"
                style={{ textTransform: 'none', fontWeight: 500, alignSelf: 'flex-start' }}>
                {r.cancelled ? 'Отменено' : `Не удалось: ${r.error ?? '?'}`}
            </Badge>
        );
    }

    return (
        <Paper radius="md" p="sm" withBorder style={{ borderColor: 'var(--mantine-color-blue-4)' }}>
            <Stack gap={6}>
                <Text fz="xs" fw={600} c="blue.6">
                    {isIssue ? 'Создать GitHub issue?' : 'Опубликовать комментарий?'}
                </Text>
                {isIssue ? (
                    <>
                        <Text fz="sm" fw={600}>{String(args.title ?? '')}</Text>
                        {typeof args.body === 'string' && args.body && (
                            <Text fz="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap' }} lineClamp={8}>
                                {args.body}
                            </Text>
                        )}
                        {Array.isArray(args.labels) && args.labels.length > 0 && (
                            <Group gap={4}>
                                {(args.labels as string[]).map(l => (
                                    <Badge key={l} size="xs" variant="light" style={{ textTransform: 'none' }}>{l}</Badge>
                                ))}
                            </Group>
                        )}
                    </>
                ) : (
                    <>
                        {typeof args.target === 'string' && args.target && (
                            <Badge size="xs" variant="light" style={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                                {args.target}
                            </Badge>
                        )}
                        <Text fz="xs" style={{ whiteSpace: 'pre-wrap' }}>{String(args.text ?? '')}</Text>
                    </>
                )}
                <Group gap={8} mt={2}>
                    <Button size="xs" loading={busy} onClick={onPublish}>
                        {isIssue ? 'Создать' : 'Опубликовать'}
                    </Button>
                    <Button size="xs" variant="subtle" color="gray" disabled={busy} onClick={onCancel}>
                        Отмена
                    </Button>
                </Group>
            </Stack>
        </Paper>
    );
}

// ─── DrawerContent: вкладки Чат / Обсуждения ───────────────────────────────

function DrawerContent({ ctx, onClose, externalPrefill }: {
    ctx: ChatPageCtx;
    onClose: () => void;
    externalPrefill?: { seq: number; prefill: string };
}) {
    const { owner, repo, path, pageContext } = ctx;
    const [activeTab, setActiveTab] = useState<string | null>('chat');
    const [chatPrefill, setChatPrefill] = useState<string | undefined>();
    const target = deriveTarget(pageContext);

    const handleSwitchToChat = useCallback((prefill: string) => {
        setChatPrefill(prefill);
        setActiveTab('chat');
    }, []);

    useEffect(() => {
        if (externalPrefill) handleSwitchToChat(externalPrefill.prefill);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalPrefill?.seq]);

    const commentsQ = useQuery({
        queryKey:             ['comments', owner, repo, path],
        queryFn:              () => fetchComments({ owner, repo, path }),
        staleTime:            0,
        refetchInterval:      20_000,
        refetchOnWindowFocus: true,
    });
    const commentCount = commentsQ.data?.comments.length ?? 0;

    return (
        <Flex direction="column" style={{ height: '100%', overflow: 'hidden' }}>
            {/* Шапка */}
            {/* Шапка в одну строку: заголовок сжимается с многоточием, крестик
               не уезжает под него. Без wrap="nowrap" + minWidth:0 длинное имя
               сущности («Юнит."Финансовый контроль для ресторанов"») ломало
               верстку — CloseButton переносился на вторую строку. */}
            <Group px="md" py="sm" justify="space-between" align="center" wrap="nowrap" gap="xs"
                style={{ borderBottom: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}>
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    <IconMessageCircle size={16} style={{ flexShrink: 0 }} />
                    <Text fw={600} fz="sm" truncate title={targetLabel(target)}>
                        {targetLabel(target)}
                    </Text>
                </Group>
                <CloseButton size="sm" onClick={onClose} style={{ flexShrink: 0 }} />
            </Group>

            {/* Вкладки */}
            <Tabs value={activeTab} onChange={setActiveTab}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                styles={{ root: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
                          panel: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
                <Tabs.List px="md" style={{ flexShrink: 0 }}>
                    <Tabs.Tab value="chat" leftSection={<IconMessageCircle size={14} />}>
                        Чат
                    </Tabs.Tab>
                    <Tabs.Tab value="comments"
                        leftSection={<IconMessages size={14} />}
                        rightSection={commentCount > 0
                            ? <Badge size="xs" circle variant="filled">{commentCount}</Badge>
                            : undefined}>
                        Обсуждения
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="chat">
                    <ChatCore owner={owner} repo={repo} path={path}
                        pageContext={pageContext} prefill={chatPrefill} />
                </Tabs.Panel>

                <Tabs.Panel value="comments">
                    <ScrollArea style={{ flex: 1, minHeight: 0 }}>
                        <CommentsTab owner={owner} repo={repo} path={path} target={target}
                            onSwitchToChat={handleSwitchToChat} />
                    </ScrollArea>
                </Tabs.Panel>
            </Tabs>
        </Flex>
    );
}

// ─── FAB ────────────────────────────────────────────────────────────────────

function Fab({ onClick, unread }: { onClick: () => void; unread: boolean }) {
    return (
        <Box style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 250 }}>
            <Tooltip label={unread ? 'Ответ готов' : 'Чат и обсуждения'} position="left">
                <Indicator disabled={!unread} color="red" size={12} offset={6} processing>
                    <ActionIcon size={52} radius="xl" variant="filled" color="blue"
                        onClick={onClick} aria-label="Открыть чат"
                        data-tour="chat-launcher">
                        <IconMessageCircle size={24} />
                    </ActionIcon>
                </Indicator>
            </Tooltip>
        </Box>
    );
}

// ─── GlobalChatDrawer (единственный экспорт, монтируется в App) ─────────────

export function GlobalChatDrawer(): JSX.Element | null {
    const ctx      = useChatStore(s => s.ctx);
    const open     = useChatStore(s => s.open);
    const unread   = useChatStore(s => s.unread);
    const setOpen  = useChatStore(s => s.setOpen);
    const isMobile = useMediaQuery('(max-width: 768px)');
    const location = useLocation();

    // Внешний запрос открытия («спросить агента», выделение → действие).
    const chatRequest = useUiStore(s => s.chatRequest);
    useEffect(() => {
        if (chatRequest) setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatRequest]);

    // Мобайл: карточка и чат не помещаются вместе — ЛЮБОЙ клик по сущности
    // (cardOpenSeq: включая повторный по уже открытой) сворачивает чат,
    // карточка выходит на передний план. Диалог сохраняется. Десктоп:
    // сосуществуют — карточка немодальна и сдвигается влево (EntityCard).
    const cardOpenSeq = useUiStore(s => s.cardOpenSeq);
    useEffect(() => {
        if (cardOpenSeq > 0 && isMobile) setOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardOpenSeq]);

    // Чат живёт только на страницах репо (не на HomePage/admin).
    if (!ctx || !location.pathname.startsWith('/r/')) return null;

    // ЕДИНЫЙ контейнер (Box, не Mantine Drawer) с условными стилями:
    // mobile = нижний лист поверх карточки (zIndex 320), desktop = правый
    // сайдбар (zIndex 150). Тип элемента НЕ меняется при повороте —
    // ChatCore/useChat не ремаунтятся, диалог и стрим переживают поворот.
    const panelStyle: React.CSSProperties = isMobile
        ? {
            position: 'fixed', left: 0, right: 0, bottom: 0, height: '85vh',
            zIndex: 320, borderTopLeftRadius: 12, borderTopRightRadius: 12,
            borderTop: '1px solid var(--mantine-color-default-border)',
            boxShadow: '0 -4px 24px rgba(0,0,0,.12)',
        }
        : {
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 400,
            zIndex: 150, borderLeft: '1px solid var(--mantine-color-default-border)',
            boxShadow: '-4px 0 24px rgba(0,0,0,.08)',
        };

    return (
        <>
            {!open && <Fab onClick={() => setOpen(true)} unread={unread} />}
            <Box style={{
                ...panelStyle,
                backgroundColor: 'var(--mantine-color-body)',
                display:         open ? 'flex' : 'none',
                flexDirection:   'column',
                overflow:        'hidden',
            }}>
                <DrawerContent
                    key={`${ctx.owner}/${ctx.repo}`}
                    ctx={ctx}
                    onClose={() => setOpen(false)}
                    externalPrefill={chatRequest ?? undefined}
                />
            </Box>
        </>
    );
}
